import React, { useState, useEffect } from "react";
import { APISelector, getSelector } from "../../utils/miscEndpoints";
import SelectorStartView from "./SelectorStartView";
import SelectorSelectView from "./SelectorSelectView";
import UserTerminalComponent from "./UserTerminal";
import LoadingUserComponent from "./LoadingUser";
import { useContentSize, saveContentSize } from "../../hooks/useContentSize";

interface Props {
  selectorId: string;
  onContinue: (organizationId: string, resLinkParamId: string) => void;
}

type SelectorStage = "loading" | "start" | "select" | "error";

const SelectorContainer: React.FC<Props> = ({ selectorId, onContinue }) => {
  const [stage, setStage] = useState<SelectorStage>("loading");
  const [selector, setSelector] = useState<APISelector | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const { ref: contentRef, size: contentSize } = useContentSize();

  const handleStart = () => {
    saveContentSize(contentRef);
    setStage("select");
  };

  const handleContinue = (resLinkParamId: string) => {
    if (selector) {
      saveContentSize(contentRef);
      onContinue(selector.organization_id, resLinkParamId);
    }
  };

  useEffect(() => {
    if (stage !== "loading") {
        return;
    }
    
    const loadSelector = async() => {
      try {
        const sel = await getSelector(selectorId);
        if (sel) {
          setSelector(sel);
          if (sel.pre_selection_copy) {
            setStage("start");
          } else {
            setStage("select");
          }
        }
      } catch (error) {
        setError("Invalid selector.");
        setStage("error");
      }
    }

    loadSelector();
  }, [selectorId, stage]);

  return (
    <div ref={contentRef}>
      {stage === "error" ? (
        <UserTerminalComponent
          reason={error}
        />
      ) : stage === "loading" ? (
        <div className="selector-loading-container">
          <LoadingUserComponent />
        </div>
      ) : stage === "start" ? (
        <SelectorStartView
          caption={selector!.pre_selection_copy}
          onContinue={handleStart}
        />
      ) : (
        <SelectorSelectView
          selector={selector!}
          onContinue={handleContinue}
        />
      )}
    </div>
  );
};

export default SelectorContainer;
